/**
 * @component OrderCapacitySettings
 *
 * The SIB's capacity per service with an effective date (SIB-941).
 *
 * One table row per service (SIB-1119): the capacity in effect, the planned capacity with its
 * date, and the actions on it. Values are shown, never typed, until the SIB plans or edits a
 * change; the editor then opens under the row. A change to an existing capacity needs its own
 * date per service, is confirmed in plain text before it is saved, and only the services that
 * actually changed are sent. A planned change can be edited or dropped while its date is still in
 * the future; once the date has arrived it is history and only a new change can follow.
 *
 * There is one view, not one per year (SIB-1122): the date of a change says which year it is for.
 * Once Union opens the next year (SIB-928) a notice says so and names the services that have no
 * value recorded for it yet. Nothing is written for the new year unless the SIB saves a change
 * dated into it; an unchanged value simply continues.
 */

import {
  Alert,
  Box,
  Button,
  Collapsible,
  Field,
  HStack,
  IconButton,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
} from '@chakra-ui/react'
import { LuMinus, LuPlus } from 'react-icons/lu'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import {
  dropPlannedCapacityChange,
  getCapacityChangeHistory,
  getOrderCapacities,
  getOrderCapacityYears,
  updateOrderCapacities,
} from '@/api/settings.api'
import type {
  CapacityChangeDto,
  OrderCapacityYearsResponse,
  UpdateOrderSettingsDto,
} from '@/api/dto/settings.dto'
import type { CapacityYear, OrderSettings } from '@/common/settings.common'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'
import { useUserStore } from '@/stores/user.store'
import {
  buildHistoryRows,
  backendMessageOf,
  formatIsoDateDE,
  isAlreadyInEffectError,
  isBaselineDate,
  parseCapacityInput,
  todayInBusinessZone,
  validateEffectiveDate,
  type HistoryRow,
  type IsoDate,
  type PendingValueChange,
  type PlannedValue,
} from '@/helper/valueChange.helper'
import { SettingsActionBar } from './SettingsActionBar'
import { SettingsInput } from './SettingsControls'
import { FormLayout } from '@/components/layout/FormLayout'
import { EffectiveDatePicker } from '@/components/settings/EffectiveDatePicker'
import { ServiceCapacityTable } from '@/components/settings/ServiceCapacityTable'
import { ValueChangeConfirmDialog } from '@/components/settings/ValueChangeConfirmDialog'
import { DropPlannedChangeDialog } from '@/components/settings/DropPlannedChangeDialog'
import { ValueChangeHistoryTable } from '@/components/settings/ValueChangeHistoryTable'

type ServiceCode = 'TOJ' | 'CONSULTING' | 'TRAINING' | 'PILOT'

const SERVICES: {
  code: ServiceCode
  label: string
  required: boolean
  helper: string
}[] = [
  {
    code: 'TOJ',
    label: 'TOJ',
    required: true,
    helper: 'Training on the Job mit oder ohne Kunde in Einheiten (1 Einheit = 2,5 h)',
  },
  {
    code: 'CONSULTING',
    label: 'Beratung',
    required: true,
    helper: 'Beratung in Stunden, es sind halbe Einheiten möglich (1 Einheit = 1 h)',
  },
  {
    code: 'TRAINING',
    label: 'Schulung',
    required: true,
    helper: 'Schulung in Tagen, es sind halbe Einheiten möglich (1 Einheit = 8 h)',
  },
  {
    code: 'PILOT',
    label: 'Pilot',
    required: false,
    helper: 'Pilot-Einheiten (1 Einheit = 1 h, nur ganze Zahlen)',
  },
]

const SERVICE_LABEL: Record<string, string> = Object.fromEntries(
  SERVICES.map(s => [s.code, s.label])
)

/** What the backend knows about one service. */
type ServiceRecord = {
  setting: OrderSettings | null
  activeValue: number | null
  activeSince: string | null
  planned: PlannedValue[]
}

/** What the user typed for one service. */
type ServiceDraft = {
  input: string
  validFrom: IsoDate | ''
  editing: PlannedValue | null
}

type Records = Record<ServiceCode, ServiceRecord>
type Drafts = Record<ServiceCode, ServiceDraft>

const EMPTY_RECORD: ServiceRecord = {
  setting: null,
  activeValue: null,
  activeSince: null,
  planned: [],
}

function toRecords(settings: OrderSettings[]): Records {
  const out = {} as Records
  for (const s of SERVICES) {
    const setting = settings.find(x => x.service?.code === s.code) ?? null
    out[s.code] = setting
      ? {
          setting,
          activeValue: setting.capacity,
          activeSince: setting.validFrom ?? null,
          planned: (setting.plannedChanges ?? []).map(p => ({
            id: p.id,
            value: p.capacity,
            validFrom: p.validFrom,
            createdBy: p.createdBy,
            createdAt: p.createdAt,
          })),
        }
      : EMPTY_RECORD
  }
  return out
}

function baselineInput(record: ServiceRecord): string {
  return record.activeValue === null ? '' : String(record.activeValue)
}

function toDrafts(records: Records): Drafts {
  const out = {} as Drafts
  for (const s of SERVICES) {
    out[s.code] = { input: baselineInput(records[s.code]), validFrom: '', editing: null }
  }
  return out
}

/** A change to an existing capacity must name its date; the very first value takes effect now. */
function needsEffectiveDate(record: ServiceRecord): boolean {
  return !!record.setting?.validFrom
}

/** Whether the SIB has to pick a date for this change; editing a planned change always does. */
function needsPickedDate(record: ServiceRecord, draft: ServiceDraft): boolean {
  return !!draft.editing || needsEffectiveDate(record)
}

function isDirty(record: ServiceRecord, draft: ServiceDraft): boolean {
  if (draft.editing) {
    return (
      draft.input.trim() !== String(draft.editing.value) ||
      draft.validFrom !== draft.editing.validFrom
    )
  }
  return draft.input.trim() !== baselineInput(record)
}

function toHistoryRows(changes: CapacityChangeDto[]): HistoryRow[] {
  return buildHistoryRows(
    changes.map(c => ({
      id: c.id,
      serviceCode: c.service?.code ?? '',
      serviceLabel:
        SERVICE_LABEL[c.service?.code ?? ''] ?? c.service?.name ?? c.service?.code ?? '',
      value: c.capacity,
      validFrom: c.validFrom,
      state: c.state,
      createdBy: c.createdBy,
      createdAt: c.createdAt,
      discardedBy: c.discardedBy,
      discardedAt: c.discardedAt,
    }))
  )
}

/** The next planning year Union has opened, if any (SIB-928). */
function nextOpenYearOf(
  res: OrderCapacityYearsResponse | null,
  currentYear: number
): CapacityYear | null {
  return (
    (res?.years ?? [])
      .filter(y => y.open && y.year > currentYear)
      .sort((a, b) => a.year - b.year)[0] ?? null
  )
}

/**
 * The last date a change may be planned for: 1 December of the latest open year, since a version
 * starts on the first of a month (SIB-928). The running year is always open, and a year that
 * already holds a planned change stays reachable so that plan can still be edited. Null while the
 * years are unknown; the backend then still refuses a year Union has not opened.
 */
function horizonEndOf(
  res: OrderCapacityYearsResponse | null,
  currentYear: number,
  records: Records
): IsoDate | null {
  if (!res) return null
  const plannedYears = SERVICES.flatMap(s =>
    records[s.code].planned.map(p => Number(p.validFrom.slice(0, 4)))
  )
  const openYears = res.years.filter(y => y.open).map(y => y.year)
  const lastOpen = Math.max(currentYear, ...openYears, ...plannedYears)
  return `${lastOpen}-12-01`
}

async function loadCapacityYears(): Promise<OrderCapacityYearsResponse | null> {
  try {
    return await getOrderCapacityYears()
  } catch {
    // Without the years the screen works as before SIB-928, just without the next-year notice.
    return null
  }
}

export function OrderCapacitySettings({ today }: { today?: IsoDate } = {}) {
  const todayIso = today ?? todayInBusinessZone()
  const currentYear = Number(todayIso.slice(0, 4))
  const user = useUserStore(s => s.user)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const [records, setRecords] = useState<Records>(() => toRecords([]))
  const [drafts, setDrafts] = useState<Drafts>(() => toDrafts(toRecords([])))
  // Services whose editor the SIB opened with "Volumenänderung planen" but hasn't typed in yet; a
  // row with a draft that differs from its record, or an edited plan, is open anyway (SIB-1119).
  const [planningCodes, setPlanningCodes] = useState<ReadonlySet<ServiceCode>>(() => new Set())

  const [nextYear, setNextYear] = useState<CapacityYear | null>(null)
  const [horizonEnd, setHorizonEnd] = useState<IsoDate | null>(null)

  const [history, setHistory] = useState<HistoryRow[]>([])
  const [historyAvailable, setHistoryAvailable] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  const [dropTarget, setDropTarget] = useState<{
    code: ServiceCode
    planned: PlannedValue
  } | null>(null)
  const [dropping, setDropping] = useState(false)

  /* ----------------------------- LOAD DATA ----------------------------- */

  const load = useCallback(async () => {
    const [res, years] = await Promise.all([getOrderCapacities(), loadCapacityYears()])
    const current = toRecords(res.settings ?? [])
    setRecords(current)
    setNextYear(nextOpenYearOf(years, currentYear))
    setHorizonEnd(horizonEndOf(years, currentYear, current))
    setDrafts(toDrafts(current))
    setPlanningCodes(new Set())

    try {
      const h = await getCapacityChangeHistory()
      setHistory(toHistoryRows(h.changes ?? []))
      setHistoryAvailable(true)
    } catch {
      setHistory([])
      setHistoryAvailable(false)
    }
  }, [currentYear])

  useEffect(() => {
    let cancelled = false
    async function run() {
      try {
        setLoading(true)
        await load()
      } catch {
        if (!cancelled) {
          toast.error(
            'Kapazitäten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.'
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [load])

  /* ---------------------------- VALIDATION ---------------------------- */

  const dirtyCodes = useMemo(
    () => SERVICES.map(s => s.code).filter(code => isDirty(records[code], drafts[code])),
    [records, drafts]
  )
  const dirty = dirtyCodes.length > 0

  const fieldProblems = useMemo(() => {
    const out = {} as Record<ServiceCode, { value: boolean; date: boolean }>
    for (const s of SERVICES) {
      const record = records[s.code]
      const draft = drafts[s.code]
      const changed = isDirty(record, draft)
      const valueProblem = changed && parseCapacityInput(draft.input) === null
      const dateProblem =
        changed &&
        needsPickedDate(record, draft) &&
        validateEffectiveDate(draft.validFrom, todayIso, horizonEnd) !== null
      out[s.code] = { value: valueProblem, date: dateProblem }
    }
    return out
  }, [records, drafts, todayIso, horizonEnd])

  /** The values that will be in effect once everything dirty is saved (unchanged ones stay). */
  const resultingValues = useMemo(
    () =>
      SERVICES.map(s => {
        const record = records[s.code]
        const draft = drafts[s.code]
        if (isDirty(record, draft)) return parseCapacityInput(draft.input) ?? 0
        return record.activeValue ?? 0
      }),
    [records, drafts]
  )
  const hasAtLeastOnePositive = resultingValues.some(n => n > 0)

  const allFieldsValid = SERVICES.every(
    s => !fieldProblems[s.code].value && !fieldProblems[s.code].date
  )
  const allValid = allFieldsValid && hasAtLeastOnePositive

  const pendingChanges: PendingValueChange[] = useMemo(
    () =>
      dirtyCodes.map(code => {
        const record = records[code]
        const draft = drafts[code]
        return {
          serviceCode: code,
          serviceLabel: SERVICE_LABEL[code],
          activeValue: record.activeValue,
          newValue: parseCapacityInput(draft.input) ?? 0,
          // null only for a first value, which takes effect now
          validFrom: needsPickedDate(record, draft) ? draft.validFrom || null : null,
          replaces: draft.editing,
        }
      }),
    [dirtyCodes, records, drafts]
  )

  /* ------------------------------ ACTIONS ------------------------------ */

  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(dirty)

  function updateDraft(code: ServiceCode, patch: Partial<ServiceDraft>) {
    setDrafts(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }))
  }

  function resetToOriginal() {
    setDrafts(toDrafts(records))
    setPlanningCodes(new Set())
    setShowErrors(false)
  }

  /** Opens the row's editor with the value in effect, so the SIB types the new volume and date. */
  function startPlanning(code: ServiceCode) {
    setPlanningCodes(prev => new Set(prev).add(code))
  }

  function startEditing(code: ServiceCode, planned: PlannedValue) {
    updateDraft(code, {
      input: String(planned.value),
      validFrom: planned.validFrom,
      editing: planned,
    })
  }

  /** Closes the row's editor and drops whatever was typed there. */
  function closeEditor(code: ServiceCode) {
    updateDraft(code, { input: baselineInput(records[code]), validFrom: '', editing: null })
    setPlanningCodes(prev => {
      const next = new Set(prev)
      next.delete(code)
      return next
    })
  }

  function isEditorOpen(code: ServiceCode) {
    return (
      planningCodes.has(code) || !!drafts[code].editing || isDirty(records[code], drafts[code])
    )
  }

  function handleSaveClick() {
    setShowErrors(true)
    if (!allValid || !dirty) return
    setConfirmOpen(true)
  }

  async function reloadAfterTakingEffect() {
    toast.info(
      'Die Änderung ist inzwischen wirksam',
      'Das Datum dieser Änderung ist erreicht; sie kann nicht mehr bearbeitet oder verworfen werden. Die Ansicht wurde aktualisiert.'
    )
    try {
      await load()
    } catch {
      /* the toast already explains the state; the next reload will settle it */
    }
  }

  async function doSave() {
    if (saving || pendingChanges.length === 0) return
    setSaving(true)

    const payload: UpdateOrderSettingsDto = {
      settings: pendingChanges.map(c => ({
        serviceCode: c.serviceCode,
        capacity: c.newValue,
        ...(c.validFrom ? { validFrom: c.validFrom } : {}),
      })),
    }

    try {
      await updateOrderCapacities(payload)

      // Editing a planned change onto a different date: the backend replaces a plan only for the
      // same date, so the old plan is dropped explicitly. Dropping after writing means a failure
      // here leaves two visible plans rather than none.
      const leftovers: string[] = []
      for (const c of pendingChanges) {
        if (c.replaces && c.replaces.validFrom !== c.validFrom) {
          try {
            await dropPlannedCapacityChange(c.replaces.id)
          } catch {
            leftovers.push(`${c.serviceLabel} ab ${formatIsoDateDE(c.replaces.validFrom)}`)
          }
        }
      }

      setConfirmOpen(false)
      const effective = pendingChanges
        .map(c =>
          c.validFrom
            ? `${c.serviceLabel} gilt ab ${formatIsoDateDE(c.validFrom)}`
            : `${c.serviceLabel} gilt ab sofort`
        )
        .join(', ')
      toast.success('Änderung gespeichert', `${effective}.`)
      if (leftovers.length > 0) {
        toast.warning(
          'Bisherige Planung noch vorhanden',
          `Die bisher geplante Änderung (${leftovers.join(', ')}) konnte nicht verworfen werden. Bitte verwerfen Sie sie manuell.`
        )
      }
      await load()
      setShowErrors(false)
    } catch (err) {
      setConfirmOpen(false)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Kapazitäten konnten nicht gespeichert werden',
          backendMessageOf(err) ?? 'Bitte versuchen Sie es erneut.'
        )
      }
    } finally {
      setSaving(false)
    }
  }

  async function doDrop() {
    if (!dropTarget || dropping) return
    setDropping(true)
    try {
      await dropPlannedCapacityChange(dropTarget.planned.id)
      toast.success(
        'Geplante Änderung verworfen',
        `${SERVICE_LABEL[dropTarget.code]}: Der aktuelle Wert gilt weiterhin.`
      )
      setDropTarget(null)
      await load()
    } catch (err) {
      setDropTarget(null)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Geplante Änderung konnte nicht verworfen werden',
          backendMessageOf(err) ?? 'Bitte versuchen Sie es erneut.'
        )
      }
    } finally {
      setDropping(false)
    }
  }

  /** Whether a planned change on screen is dated into a year after the running one (SIB-1119). */
  const hasLaterYearChanges = useMemo(
    () =>
      SERVICES.some(s =>
        records[s.code].planned.some(p => Number(p.validFrom.slice(0, 4)) > currentYear)
      ),
    [records, currentYear]
  )

  /* ------------------------------ EDITOR ------------------------------- */

  /** The value and date fields that open under a service's row once a change is planned. */
  function renderEditor(code: ServiceCode, label: string) {
    const record = records[code]
    const draft = drafts[code]
    const changed = isDirty(record, draft)
    const problems = fieldProblems[code]
    const valueError = showErrors && problems.value
    const dateRequired = changed && needsPickedDate(record, draft)
    const inputLabel = draft.editing
      ? `Geplanter Wert ab ${formatIsoDateDE(draft.editing.validFrom)}`
      : record.activeValue === null
        ? 'Wert'
        : 'Neuer Wert'

    return (
      <Stack gap={4}>
        <Box
          display="grid"
          gap={4}
          alignItems="flex-start"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        >
          <Field.Root invalid={valueError} required>
            <Field.Label>
              {inputLabel} <Field.RequiredIndicator />
            </Field.Label>
            <SettingsInput
              value={draft.input}
              onChange={e => updateDraft(code, { input: e.target.value })}
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              disabled={saving}
              aria-label={`${label} ${inputLabel}`}
              placeholder=""
            />
            {valueError && (
              <Field.ErrorText>
                Wert darf nicht leer sein und muss eine ganze Zahl ≥ 0 sein.
              </Field.ErrorText>
            )}
          </Field.Root>

          {(dateRequired || draft.editing) && (
            <EffectiveDatePicker
              name={`validFrom-${code}`}
              value={draft.validFrom}
              onChange={v => updateDraft(code, { validFrom: v })}
              required={dateRequired}
              showError={showErrors}
              disabled={saving}
              today={todayIso}
              latest={horizonEnd}
              helperText="Änderungen gelten immer ab dem 1. eines Monats."
            />
          )}
        </Box>

        <HStack gap={3} flexWrap="wrap">
          <Button variant="ghost" onClick={() => closeEditor(code)} disabled={saving}>
            {draft.editing ? 'Bearbeitung abbrechen' : 'Abbrechen'}
          </Button>
        </HStack>
      </Stack>
    )
  }

  /* ------------------------------ RENDER ------------------------------- */

  return (
    <Box data-testid="order-capacity-settings" position="relative">
      {/* guard dialog (route change) */}
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={() => {
          resetToOriginal()
          confirmNavigation()
        }}
        onSave={() => {
          // Saving always goes through the confirmation; the page stays until it is done.
          handleSaveClick()
        }}
        onNavigate={cancelNavigation}
      />

      <FormLayout.Root>
        <FormLayout.Section>
          <FormLayout.Title>Auftragsvolumen eintragen</FormLayout.Title>
          <FormLayout.Description>
            Bitte tragen Sie hier Ihr Auftragsvolumen je Leistung ein. Eine Änderung gilt ab dem
            gewählten Datum; bis dahin bleibt der aktuelle Wert bestehen.
          </FormLayout.Description>
        </FormLayout.Section>

        {!loading && nextYear && <NextYearNotice year={nextYear} />}

        {loading ? (
          <Stack w="full" gap={3} data-testid="capacity-table-loading">
            <SkeletonText noOfLines={1} w="240px" />
            {SERVICES.map(s => (
              <Skeleton key={s.code} h="44px" w="full" />
            ))}
          </Stack>
        ) : (
          <Stack gap={4}>
            <ServiceCapacityTable
              visibleYear={currentYear}
              disabled={saving}
              rows={SERVICES.map(s => {
                const record = records[s.code]
                const draft = drafts[s.code]
                const editorOpen = isEditorOpen(s.code)
                return {
                  key: s.code,
                  label: s.label,
                  helper: s.helper,
                  required: s.required,
                  activeValue: record.activeValue,
                  activeNote: record.activeSince
                    ? isBaselineDate(record.activeSince)
                      ? '(von Anfang an)'
                      : `(seit ${formatIsoDateDE(record.activeSince)})`
                    : null,
                  planned: record.planned,
                  editingId: draft.editing?.id ?? null,
                  editorOpen,
                  editor: editorOpen ? renderEditor(s.code, s.label) : null,
                  onPlan: () => startPlanning(s.code),
                  onEditPlanned: (p: PlannedValue) => startEditing(s.code, p),
                  onDropPlanned: (p: PlannedValue) => setDropTarget({ code: s.code, planned: p }),
                }
              })}
            />

            {hasLaterYearChanges && (
              <Text color="fg.muted" data-testid="later-year-note">
                Änderungen mit einem Datum im Folgejahr sind hier bereits aufgeführt und gelten
                dort ab dem genannten Datum.
              </Text>
            )}

            {!hasAtLeastOnePositive && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Description>
                  Mindestens eine Kapazität muss größer als 0 sein.
                </Alert.Description>
              </Alert.Root>
            )}
          </Stack>
        )}

        {!loading && (
          <Collapsible.Root open={historyOpen} onOpenChange={e => setHistoryOpen(e.open)}>
            <FormLayout.Section>
              <HStack gap={2}>
                <FormLayout.Title>Verlauf</FormLayout.Title>
                <Collapsible.Trigger asChild>
                  <IconButton
                    variant="ghost"
                    aria-label={historyOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
                    aria-expanded={historyOpen}
                  >
                    {historyOpen ? <LuMinus /> : <LuPlus />}
                  </IconButton>
                </Collapsible.Trigger>
              </HStack>
              <FormLayout.Description>
                Ihre bisherigen Änderungen mit altem und neuem Wert, Datum und Bearbeiter.
              </FormLayout.Description>
              <Collapsible.Content>
                {historyAvailable ? (
                  <ValueChangeHistoryTable kind="capacity" rows={history} currentUser={user} />
                ) : (
                  <Text color="fg.muted">Der Verlauf ist derzeit nicht verfügbar.</Text>
                )}
              </Collapsible.Content>
            </FormLayout.Section>
          </Collapsible.Root>
        )}

        {dirty && !loading && (
          <SettingsActionBar
            visible={dirty && !loading}
            onCancel={() => setCancelOpen(true)}
            cancelDisabled={saving}
            onSave={handleSaveClick}
            saveDisabled={!allValid || saving}
            saving={saving}
          />
        )}
      </FormLayout.Root>

      {/* explicit cancel from the action bar */}
      <DismissChangesDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onDismiss={() => {
          resetToOriginal()
          setCancelOpen(false)
        }}
        onSave={() => {
          setCancelOpen(false)
          handleSaveClick()
        }}
        onNavigate={() => {
          // no tab navigation from this page – kept for API compatibility
        }}
      />

      <ValueChangeConfirmDialog
        isOpen={confirmOpen}
        kind="capacity"
        changes={pendingChanges}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doSave}
        saving={saving}
      />

      <DropPlannedChangeDialog
        isOpen={dropTarget !== null}
        kind="capacity"
        serviceLabel={dropTarget ? SERVICE_LABEL[dropTarget.code] : ''}
        planned={dropTarget?.planned ?? null}
        activeValue={dropTarget ? records[dropTarget.code].activeValue : null}
        onCancel={() => setDropTarget(null)}
        onConfirm={doDrop}
        dropping={dropping}
      />
    </Box>
  )
}

/**
 * What the SIB needs to know once Union has opened the next year (SIB-928, SIB-1122): the current
 * values continue unless changed, and which required services have no value recorded for the
 * year yet. A carried-over value must never look like a decision - Union reads this to see which
 * SIBs have planned the new year - so the services are named until a change dated into the year
 * is saved.
 */
function NextYearNotice({ year }: { year: CapacityYear }) {
  // An optional service the SIB never offered (Pilot without a value) is nothing left to plan.
  const notPlanned = SERVICES.filter(s => {
    const source = year.services.find(x => x.service?.code === s.code)?.source ?? 'NONE'
    return source === 'CARRIED_OVER' || (source === 'NONE' && s.required)
  })
  const firstOfYear = formatIsoDateDE(`${year.year}-01-01`)

  return (
    <Alert.Root
      status={notPlanned.length > 0 ? 'warning' : 'success'}
      data-testid="next-year-notice"
    >
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{year.year} ist zur Planung geöffnet.</Alert.Title>
        <Alert.Description>
          {notPlanned.length > 0
            ? `Für ${notPlanned.length === 1 ? '1 Leistung' : `${notPlanned.length} Leistungen`} liegt noch kein Wert für ${year.year} vor: ${notPlanned.map(s => s.label).join(', ')}. Ohne Änderung gilt der aktuelle Wert weiter. Eine Änderung mit Datum ${firstOfYear} gilt als Wert für ${year.year}.`
            : `Alle Leistungen sind für ${year.year} erfasst. Weitere Änderungen planen Sie mit einem Datum im Jahr ${year.year}.`}
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  )
}
